import React, { useState } from "react";
import { Form, Button, Modal, Checkbox, Message, Icon } from "semantic-ui-react";
import { disposalMethods } from "../../../../utils/dataAndFormulas/wasteForms";
import { useLanguage } from "../../../../contexts";
import "./UpdateDisposalMethodModal.scss";

export function UpdateDisposalMethodModal(props) {
  const { show, onClose, onApply, currentDisposalMethods, registeredFields, isLoading, missingCounts } = props;
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [selectedFields, setSelectedFields] = useState({});
  const [fieldMethods, setFieldMethods] = useState({});

  const disposalMethodOptions = disposalMethods.map((ds) => ({
    key: ds,
    text: t(ds),
    value: ds,
  }));

  const allWasteFields = [
    { key: "paper_and_cardboard", label: t("paper_and_cardboard") },
    { key: "plastic", label: t("plastic") },
    { key: "fabric_scraps", label: t("fabric_scraps") },
    { key: "metal_clean", label: t("metal_clean") },
    { key: "wood", label: t("wood") },
    { key: "food_waste", label: t("food_waste") },
    { key: "general_trash", label: t("general_trash") },
    { key: "glass", label: t("glass") },
    { key: "leather", label: t("leather") },
    { key: "foam_eva", label: t("foam_eva") },
    { key: "empty_cans_or_containers", label: t("empty_cans_or_containers") },
    { key: "rubber", label: t("rubber") },
    { key: "other_non_hazardous_waste", label: t("other_non_hazardous_waste") },
    { key: "batteries", label: t("batteries") },
    { key: "electronics", label: t("electronics") },
    { key: "drums_or_containers_with_product_remains", label: t("drums_or_containers_with_product_remains") },
    { key: "hospital_waste", label: t("hospital_waste") },
    { key: "fluorescent_tubes", label: t("fluorescent_tubes") },
    { key: "cooking_oil", label: t("cooking_oil") },
    { key: "maintenance_oil", label: t("maintenance_oil") },
    { key: "lubricants", label: t("lubricants") },
    { key: "paint_waste", label: t("paint_waste") },
    { key: "coal_ashes", label: t("coal_ashes") },
    { key: "rubber_hazardous", label: t("rubber_hazardous") },
    { key: "chemicals", label: t("chemicals") },
    { key: "contaminated_materials", label: t("contaminated_materials") },
    { key: "ink_cartridges", label: t("ink_cartridges") },
    { key: "medical_waste", label: t("medical_waste") },
    { key: "other_hazardous_waste", label: t("other_hazardous_waste") },
  ];

  const registeredWasteFields =
    registeredFields && registeredFields.length > 0
      ? registeredFields
          .filter((field) => field.active !== false)
          .map((field) => ({
            key: field.name,
            label: t(field.name),
            code: field.code,
          }))
      : allWasteFields;

  // Un campo puede tener datos cargados (ej. vía lectura de factura) aunque
  // nunca se haya "activado"/configurado su unidad para este sitio -- en ese
  // caso queda fuera de `registeredFields`, y si le falta método de
  // disposición, esta lista lo iba a omitir por completo: el conteo total
  // (missingCounts) lo contaba para la advertencia, pero acá no aparecía
  // para poder completarlo, dejando la advertencia sin forma de resolverse.
  const registeredKeys = new Set(registeredWasteFields.map((f) => f.key));
  const unregisteredMissingFields = Object.keys(missingCounts || {})
    .filter((key) => !registeredKeys.has(key) && missingCounts[key] > 0)
    .map((key) => ({ key, label: t(key), unregistered: true }));

  const wasteFields = [...registeredWasteFields, ...unregisteredMissingFields];

  const handleFieldToggle = (fieldKey, checked) => {
    setSelectedFields(prev => ({ ...prev, [fieldKey]: checked }));
    if (checked && !fieldMethods[fieldKey]) {
      setFieldMethods(prev => ({ ...prev, [fieldKey]: currentDisposalMethods?.[fieldKey] || "" }));
    }
  };

  const handleMethodChange = (fieldKey, method) => {
    setFieldMethods(prev => ({ ...prev, [fieldKey]: method }));
  };

  const handleSelectAll = () => {
    const allSelected = {};
    const allMethods = {};
    wasteFields.forEach(field => {
      allSelected[field.key] = true;
      allMethods[field.key] = currentDisposalMethods?.[field.key] || "";
    });
    setSelectedFields(allSelected);
    setFieldMethods(allMethods);
  };

  const handleDeselectAll = () => {
    setSelectedFields({});
  };

  const handleApply = () => {
    const methodsToApply = {};
    Object.keys(selectedFields).forEach(key => {
      if (selectedFields[key] && fieldMethods[key]) {
        methodsToApply[key] = fieldMethods[key];
      }
    });
    if (Object.keys(methodsToApply).length === 0) return;
    onApply(methodsToApply);
  };

  const selectedCount = Object.values(selectedFields).filter(Boolean).length;

  return (
    <Modal open={show} onClose={onClose} size="large" className="update-disposal-method-modal">
      <Modal.Content scrolling>
        <div className="update-disposal-method-modal__title">
          {t("update_disposal_method_in_forms") || "Completar método de disposición"}
        </div>

        <Message info>
          <p>
            {t("update_disposal_method_description") ||
              "Esto solo completa el método en los períodos que todavía no tienen uno cargado. No modifica los que ya tienen un método seleccionado."}
          </p>
        </Message>

        <div style={{ marginBottom: "15px" }}>
          <Button size="small" className="btn-select-all" onClick={handleSelectAll}>
            <Icon name="check square" /> {t("select_all") || "Seleccionar todos"}
          </Button>
          <Button size="small" className="btn-deselect-all" onClick={handleDeselectAll}>
            <Icon name="square outline" /> {t("deselect_all") || "Deseleccionar todos"}
          </Button>
          <span style={{ marginLeft: "15px", color: "#666" }}>
            {selectedCount} {t("fields_selected") || "campos seleccionados"}
          </span>
        </div>

        <Form>
          <div className="update-disposal-method-grid">
            {wasteFields.map((field) => {
              const missing = missingCounts?.[field.key] || 0;
              return (
              <div key={field.key} className="update-disposal-method-row">
                <Checkbox
                  checked={selectedFields[field.key] || false}
                  onChange={(_, data) => handleFieldToggle(field.key, data.checked)}
                  label={{
                    children: (
                      <>
                        {field.label}{" "}
                        {missing > 0 ? (
                          <span className="missing-count missing-count--pending">
                            ({missing} {t("without_disposal_method") || "sin método"})
                          </span>
                        ) : (
                          <span className="missing-count missing-count--ok">
                            ({t("all_complete") || "completo"})
                          </span>
                        )}
                        {field.unregistered && (
                          <span className="missing-count missing-count--unregistered">
                            {" "}
                            ({t("unactivated_source") || "fuente no activada"})
                          </span>
                        )}
                      </>
                    ),
                  }}
                  className="field-checkbox"
                />
                {selectedFields[field.key] && (
                  <Form.Dropdown
                    placeholder={t("select_disposal_method")}
                    options={disposalMethodOptions}
                    selection
                    search
                    compact
                    value={fieldMethods[field.key] || ""}
                    onChange={(_, data) => handleMethodChange(field.key, data.value)}
                    className="method-dropdown"
                  />
                )}
              </div>
              );
            })}
          </div>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button
          primary
          onClick={handleApply}
          disabled={selectedCount === 0 || isLoading}
          loading={isLoading}
        >
          <Icon name="save" />
          {t("apply_to_forms") || `Aplicar a formularios (${selectedCount})`}
        </Button>
        <Button secondary onClick={onClose} disabled={isLoading}>
          {t("cancel")}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
